import { useMemo } from 'react';
import { getViewTables } from '../utils';
import {
  IDataModelerDraftEdits,
  IJoinDraftData,
} from '../../../../redux/slices/dataModeler';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import {
  ISchemaDataModelFieldsFragment,
  ITableMetaFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';

export const useAllTableItems = ({
  schemaObj,
  draftEdits,
}: {
  schemaObj: ISchemaDataModelFieldsFragment;
  draftEdits: IDataModelerDraftEdits;
}) => {
  return useMemo(() => {
    const {
      editJoinsData,
      addedFactsData,
      addedDimensionsData,
      draftFieldsData,
      editColumnInputs,
      editTablesData,
    } = draftEdits;
    const factTables = getViewTables(
      schemaObj.fact.tableKeys,
      schemaObj.tables,
    );
    const joinOverrides: Record<string, IJoinDraftData | undefined> = {};
    editJoinsData.forEach(
      (editJoinData) =>
        (joinOverrides[editJoinData.viewKey] = editJoinData.joinData),
    );
    const getIsModifiedByEdits = (table: ITableMetaFieldsFragment) => {
      const editTableData = editTablesData.find(
        (editTabledata) => editTabledata.tableKey === table.key,
      );
      const isModifiedByEdits =
        !!editTableData?.sqlTableName ||
        !!editTableData?.installTemporals ||
        editColumnInputs.some(
          (editColumnInput) => editColumnInput.tableKey === table.key,
        );
      const sqlTableName = editTableData?.sqlTableName ?? table.name;
      return {
        isModifiedByEdits,
        sqlTableName,
      };
    };

    return [
      ...factTables.map((factTable) => {
        const { isModifiedByEdits, sqlTableName } =
          getIsModifiedByEdits(factTable);
        const isModified =
          isModifiedByEdits ||
          draftFieldsData.some(
            ({ viewKey, tableKey }) =>
              viewKey === schemaObj.fact.key && tableKey === factTable.key,
          );
        return {
          tableKey: factTable.key,
          sqlTableName,
          viewKey: schemaObj.fact.key,
          isFactTable: true,
          isNew: false,
          isModified,
          joinCondition: null,
          canRemove: false,
        };
      }),
      ...addedFactsData.map((factData) => {
        // XXX can not remove the last fact table if any dimensions were defined
        const canRemove =
          factTables.length + addedFactsData.length > 1 ||
          addedDimensionsData.length === 0;
        return {
          tableKey: factData.tableKey,
          sqlTableName: factData.sqlTableName,
          viewKey: schemaObj.fact.key,
          isFactTable: true,
          isNew: true,
          isModified: true,
          joinCondition: null,
          canRemove,
        };
      }),
      ...schemaObj.dimensions.map((dimView) => {
        const dimTables = getViewTables(dimView.tableKeys, schemaObj.tables);
        // XXX we currently don't support multi-table dimensions
        assertCondition(
          dimTables.length <= 1,
          "we currently don't support multi-table dimensions",
        );
        const dimTable = dimTables[0];
        assertNotNil(dimTable);
        const { isModifiedByEdits, sqlTableName } =
          getIsModifiedByEdits(dimTable);
        const isModifiedByAdd =
          isModifiedByEdits ||
          draftFieldsData.some(
            ({ addInput }) =>
              !!addInput &&
              addInput.viewKey === dimView.key &&
              addInput.tableKey === dimTable.key,
          );
        const isModifiedByJoin = !!joinOverrides[dimView.key];
        const isModified =
          isModifiedByEdits || isModifiedByAdd || isModifiedByJoin;
        return {
          tableKey: dimTable.key,
          sqlTableName,
          viewKey: dimView.key,
          isFactTable: false,
          isNew: false,
          isModified,
          joinCondition: joinOverrides[dimView.key] ?? dimView.join,
          canRemove: false,
        };
      }),
      ...addedDimensionsData.map((dimInput) => {
        return {
          tableKey: dimInput.tableKey ?? `${schemaObj.key}.${dimInput.viewKey}`,
          sqlTableName: dimInput.sqlTableName,
          viewKey: dimInput.viewKey,
          isFactTable: false,
          isNew: true,
          isModified: true,
          joinCondition: dimInput.joinData,
          canRemove: true,
        };
      }),
    ];
  }, [draftEdits, schemaObj]);
};
